import { useEffect, useState } from 'react'
import { ApiError, UNAUTHORIZED_EVENT, api } from './api/client'
import type { PropertiesResponse, SessionInfo } from './api/types'
import { clearAsyncCache, useAsync } from './api/useReport'
import { Banner } from './components/Banner'
import { ThemeToggle } from './components/ThemeToggle'
import { Briefing } from './pages/Briefing'
import { Daybook } from './pages/Daybook'
import { EventLookup } from './pages/EventLookup'
import { FunnelView } from './pages/FunnelView'
import { Login } from './pages/Login'
import { Metrics } from './pages/Metrics'
import { Paywall } from './pages/Paywall'
import { Pulse } from './pages/Pulse'
import { Settings } from './pages/Settings'
import { Setup } from './pages/Setup'
import { Admin } from './pages/Admin'
import { type Location, readLocation, writeLocation } from './routing'

/**
 * Where you are, and what you were reading when you got there.
 *
 * A view carries its own context rather than reading from a pile of shared state: the
 * point of the daybook is that opening the charts or a funnel from a day hands that
 * day over, and "which day" is a property of the destination, not of the app.
 */
type View =
  | { name: 'daybook' }
  | { name: 'metrics'; end?: string; fromDay?: string }
  | { name: 'funnel'; funnel: string | null; reportDate: string }
  | { name: 'briefing'; reportDate?: string }
  | { name: 'events' }
  | { name: 'pulse' }
  | { name: 'paywall' }
  | { name: 'settings' }
  | { name: 'admin' }

type Section = View['name']

/**
 * The address as a view, and back.
 *
 * Only the fields that make a link mean something are carried. `metrics.fromDay` is
 * deliberately not: it records that you arrived from a particular day so the view can
 * offer a way back, which is a fact about the journey rather than about the destination,
 * and putting it in a URL would make a shared link claim a history the recipient has not
 * had.
 */
function viewFromLocation(location: Location): View {
  switch (location.section) {
    case 'funnel':
      // `reportDate` is filled in from the property's own latest settled day once the
      // properties load; the empty string is "not chosen yet", not a date.
      return { name: 'funnel', funnel: location.funnel ?? null, reportDate: location.day ?? '' }
    case 'metrics':
      return { name: 'metrics', end: location.day }
    case 'briefing':
      return { name: 'briefing', reportDate: location.day }
    case 'daybook':
      return { name: 'daybook' }
    default:
      return { name: location.section } as View
  }
}

function locationFromView(view: View, property: string | null): Location {
  const base: Location = { section: view.name, property: property ?? undefined }
  if (view.name === 'funnel') {
    return { ...base, funnel: view.funnel ?? undefined, day: view.reportDate || undefined }
  }
  if (view.name === 'metrics') return { ...base, day: view.end }
  if (view.name === 'briefing') return { ...base, day: view.reportDate }
  return base
}

const NAV: { id: Section; label: string }[] = [
  { id: 'daybook', label: 'Daybook' },
  { id: 'metrics', label: 'Metrics' },
  { id: 'funnel', label: 'Funnels' },
  { id: 'events', label: 'Events' },
  // Its own section rather than a strip on the daybook: it is the only view here that
  // reads hours GA4 has not finished counting, and sitting above settled days invited
  // the one comparison it must not support. The daybook keeps a one-line recap.
  { id: 'pulse', label: 'Pulse' },
]

// Shown only for a property that runs one. A property without a paywall would get a
// tab whose every figure is zero, and "the paywall collapsed" must not render the same
// as "there is no paywall here". Like the entries below, this is a convenience rather
// than the boundary: `/api/paywall` answers 404 for a property with none, whatever the
// UI renders.
const PAYWALL_NAV: { id: Section; label: string } = { id: 'paywall', label: 'Paywall' }

// Shown only to a caller who may change the configuration. Like the admin entry, this
// is a convenience rather than the boundary: the write endpoints refuse anyone else
// whatever the UI renders.
const SETTINGS_NAV: { id: Section; label: string } = { id: 'settings', label: 'Settings' }

// Only an administrator gets this one. It is a convenience, not the boundary — the
// admin endpoints refuse anyone else regardless of what the UI renders.
const ADMIN_NAV: { id: Section; label: string } = { id: 'admin', label: 'Admin' }

function Shell({ children }: { children?: React.ReactNode }) {
  return (
    <div className="app">
      <header className="masthead">
        <div className="masthead__brand">
          <span className="masthead__title">GA4 Briefing</span>
        </div>
        <nav className="masthead__nav">
          <ThemeToggle />
        </nav>
      </header>
      {children}
    </div>
  )
}

export default function App() {
  // Seeded from the address bar, so a reload lands where the reader was rather than on
  // the daybook — and so a view can be linked to at all.
  const [view, setView] = useState<View>(() => viewFromLocation(readLocation()))
  const [days, setDays] = useState(7)
  // The event lookup keeps its own end date: it is a window view like Metrics, but
  // reached from the nav rather than from a day, so it has none to inherit.
  const [eventsEnd, setEventsEnd] = useState<string | null>(null)
  // The paywall keeps its own end date for the same reason: a window view reached from
  // the nav, with no day to inherit.
  const [paywallEnd, setPaywallEnd] = useState<string | null>(null)
  const [propertyId, setPropertyId] = useState<string | null>(() => readLocation().property ?? null)

  // Three states, not two: signed in, signed out, and "not asked yet". Rendering the
  // login form during the first request would flash it at an already-signed-in user.
  const [session, setSession] = useState<SessionInfo | null>(null)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [checked, setChecked] = useState(false)
  // Set only by the "skip" button on first-run setup, so an upgraded open deployment
  // keeps working. Not persisted: the offer returns on the next load, which is the
  // right amount of nagging for a service that is currently readable by anyone.
  const [setupSkipped, setSetupSkipped] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    api
      .session(controller.signal)
      .then(setSession)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        // A 401 is the normal signed-out answer, not a failure worth reporting.
        if (!(cause instanceof ApiError) || cause.status !== 401) {
          setSessionError(cause instanceof Error ? cause.message : 'Could not reach the server.')
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setChecked(true)
      })
    return () => controller.abort()
  }, [])

  // A session can expire while a tab is open. When it does, drop back to the login
  // form rather than letting each panel report the 401 as its own failure.
  useEffect(() => {
    const signedOut = () => {
      // Cached responses belong to the session that fetched them. Whoever signs in
      // next must not be seeded from them.
      clearAsyncCache()
      setSession(null)
    }
    window.addEventListener(UNAUTHORIZED_EVENT, signedOut)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, signedOut)
  }, [])

  // The default end date is the backend's most recent settled day, so the client
  // never has to reason about the property's reporting timezone.
  // Re-fetched when the section changes so a property added on the Admin tab appears
  // in the picker as soon as you leave it, without a reload.
  const { data: properties, error } = useAsync<PropertiesResponse>(
    (signal) => api.properties(signal),
    [session?.username, view.name],
    session !== null,
    `properties:${session?.username ?? ''}`,
  )

  async function signOut() {
    try {
      await api.logout()
    } finally {
      // Local state is dropped either way: whatever happened to the request, this
      // browser should stop showing another user's property — including the responses
      // already cached under it.
      clearAsyncCache()
      setSession(null)
      setPropertyId(null)
      setView({ name: 'daybook' })
    }
  }

  // Two directions, deliberately separate. The view is written to the address bar
  // whenever it changes, and the address bar is read back only when the *browser*
  // changes it — a popstate from back or forward. Reading it back on every change would
  // be a loop with a render in it.
  useEffect(() => {
    writeLocation(locationFromView(view, propertyId))
  }, [view, propertyId])

  useEffect(() => {
    function onPop() {
      const location = readLocation()
      setView(viewFromLocation(location))
      if (location.property) setPropertyId(location.property)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  if (!checked) return <Shell />
  if (sessionError) return <Shell><Banner tone="critical">{sessionError}</Banner></Shell>
  // No accounts anywhere: offer to create the first one rather than a login form that
  // nobody could satisfy. `session` is non-null here because open mode still answers.
  if (session?.needs_setup && !setupSkipped) {
    return <Setup onCreated={setSession} onSkip={() => setSetupSkipped(true)} />
  }
  if (!session) return <Login onSignedIn={setSession} />
  if (error) return <Shell><Banner tone="critical">{error}</Banner></Shell>
  if (!properties) return <Shell />

  const maxDate = properties.default_report_date
  const property =
    properties.properties.find((entry) => entry.property_id === propertyId) ??
    properties.properties[0]

  // Switching property can strip the tab you are standing on: a property with no
  // paywall does not offer that section at all. Falling back to the daybook is the only
  // honest answer — the endpoint would 404 and a blank panel would read as a failure —
  // and it is derived rather than pushed into state so the nav highlight follows too.
  const activeSection: Section =
    view.name === 'paywall' && !property?.has_paywall ? 'daybook' : view.name

  function open(section: Section) {
    // Entering a day-scoped section from the top nav rather than from a day means
    // there is no day to carry, so it starts on the most recent settled one.
    switch (section) {
      case 'funnel':
        return setView({ name: 'funnel', funnel: null, reportDate: maxDate })
      case 'metrics':
        return setView({ name: 'metrics' })
      case 'briefing':
        return setView({ name: 'briefing' })
      case 'events':
        return setView({ name: 'events' })
      case 'pulse':
        return setView({ name: 'pulse' })
      case 'paywall':
        return setView({ name: 'paywall' })
      case 'settings':
        return setView({ name: 'settings' })
      case 'admin':
        return setView({ name: 'admin' })
      default:
        return setView({ name: 'daybook' })
    }
  }

  return (
    <div className="app">
      <header className="masthead">
        <div className="masthead__brand">
          <span className="masthead__title">GA4 Briefing</span>
          {properties.properties.length > 1 ? (
            <label className="masthead__picker">
              <span className="visually-hidden">Property</span>
              <select
                value={property?.property_id ?? ''}
                onChange={(event) => setPropertyId(event.target.value)}
              >
                {properties.properties.map((entry) => (
                  <option key={entry.property_id} value={entry.property_id}>
                    {entry.display_name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            property && <span className="masthead__property">{property.display_name}</span>
          )}
        </div>

        <nav className="masthead__nav" aria-label="Sections">
          {[
            ...NAV,
            ...(property?.has_paywall ? [PAYWALL_NAV] : []),
            ...(session.can_configure ? [SETTINGS_NAV] : []),
            ...(session.is_admin ? [ADMIN_NAV] : []),
          ].map((entry) => (
            <button
              key={entry.id}
              type="button"
              className="masthead__link"
              aria-current={activeSection === entry.id ? 'page' : undefined}
              onClick={() => open(entry.id)}
            >
              {entry.label}
            </button>
          ))}
          <ThemeToggle />

          {session.authenticated && (
            <>
              <span className="masthead__user">{session.display_name || session.username}</span>
              <button type="button" className="link-button link-button--quiet" onClick={signOut}>
                Sign out
              </button>
            </>
          )}
        </nav>
      </header>

      {/* Keyed on the property so switching remounts rather than showing the previous
          property's numbers under the new name while the next request is in flight. */}
      {activeSection === 'daybook' && (
        <Daybook
          key={property?.property_id}
          propertyId={property?.property_id}
          onOpenMetrics={(end) => setView({ name: 'metrics', end, fromDay: end })}
          onOpenFunnel={(funnel, reportDate) => setView({ name: 'funnel', funnel, reportDate })}
          onOpenBriefing={(reportDate) => setView({ name: 'briefing', reportDate })}
          onOpenPulse={() => setView({ name: 'pulse' })}
        />
      )}

      {view.name === 'metrics' && (
        <Metrics
          key={property?.property_id}
          end={view.end ?? maxDate}
          days={days}
          maxDate={maxDate}
          propertyId={property?.property_id}
          fromDay={view.fromDay}
          onBack={() => setView({ name: 'daybook' })}
          onWindowChange={(next) => {
            if (next.days !== undefined) setDays(next.days)
            if (next.end !== undefined) {
              // Changing the date here detaches the view from the day it was opened
              // from, so the back link stops claiming a day you are no longer reading.
              setView({ name: 'metrics', end: next.end })
            }
          }}
        />
      )}

      {/* `reportDate` falls back to the latest settled day: a funnel reached by URL
          carries no day unless the link had one, and the empty string means "not
          chosen" rather than a date. */}
      {view.name === 'funnel' && (
        <FunnelView
          key={property?.property_id}
          funnelName={view.funnel}
          reportDate={view.reportDate || maxDate}
          maxDate={maxDate}
          propertyId={property?.property_id}
          onBack={() => setView({ name: 'daybook' })}
          onFunnelChange={(funnel) => setView({ ...view, funnel })}
          onDateChange={(reportDate) => setView({ ...view, reportDate })}
        />
      )}

      {view.name === 'briefing' && (
        <Briefing
          key={property?.property_id}
          propertyId={property?.property_id}
          reportDate={view.reportDate}
          onBack={() => setView({ name: 'daybook' })}
        />
      )}

      {view.name === 'events' && (
        <EventLookup
          key={property?.property_id}
          end={eventsEnd ?? maxDate}
          days={days}
          maxDate={maxDate}
          propertyId={property?.property_id}
          onWindowChange={(next) => {
            if (next.days !== undefined) setDays(next.days)
            if (next.end !== undefined) setEventsEnd(next.end)
          }}
        />
      )}

      {view.name === 'pulse' && <Pulse key={property?.property_id} propertyId={property?.property_id} />}

      {activeSection === 'paywall' && (
        <Paywall
          key={property?.property_id}
          end={paywallEnd ?? maxDate}
          days={days}
          maxDate={maxDate}
          propertyId={property?.property_id}
          onWindowChange={(next) => {
            if (next.days !== undefined) setDays(next.days)
            if (next.end !== undefined) setPaywallEnd(next.end)
          }}
        />
      )}

      {view.name === 'settings' && session.can_configure && (
        <Settings key={property?.property_id} propertyId={property?.property_id} />
      )}

      {view.name === 'admin' && session.is_admin && <Admin currentUser={session.username} />}
    </div>
  )
}
